import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-page-header>` says about itself.
 *
 * Two things about this piece are not obvious from its name and are worth stating before the
 * declarations below.
 *
 * The DESCRIPTION HAS TWO PATHS and they are not additive. `description` is an attribute shortcut,
 * and `sync()` writes it with `textContent` into the very element that holds the default slot — so
 * setting the attribute REPLACES whatever was slotted there rather than sitting beside it. Use one
 * or the other. The second example uses the slot deliberately, so both paths are on the page.
 *
 * The ACCESSIBLE NAME of the surface comes from `heading`, which is drawn into a real `<h1>`. That
 * is the only `<h1>` a surface should have, and it is the reason this piece is declared as the head
 * of a surface rather than as a heading that may be dropped anywhere.
 *
 * A decision this file had to make, stated rather than left silent: `aria-label` is not documented
 * as an input on any of these pieces. It is a standard ARIA attribute on the host element rather
 * than something the component invents, so it is covered under `accessibility`, where a reader
 * looking for it will be looking.
 */
const docs: ComponentDocs = {
  aliases: ['page title', 'list header', 'surface header', 'title bar', 'page heading'],

  answers: [
    'put a title at the top of a list',
    'say how many records this view is showing',
    'explain in a sentence what a screen is for',
    'put a New or an Export button at the top right of a page',
    'show which app a page belongs to',
  ],

  // The head of a LIST or OVERVIEW surface. A record page's head is RecordHeader, which is a
  // different shape and a different statement, so recordPage is deliberately not granted here.
  placements: ['homePage', 'appPage'],

  inputs: [
    {
      name: 'eyebrow',
      attribute: 'eyebrow',
      type: 'text',
      description:
        'The small mono label above the title, saying which app or area this surface belongs to. ' +
        'Hidden entirely when it is empty.',
    },
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description: 'The page title. Drawn as the surface’s <h1>, so it is also its accessible name.',
      required: true,
    },
    {
      name: 'count',
      attribute: 'count',
      type: 'text',
      description:
        'A record count set beside the title in the muted ink. Text rather than a number, so a host ' +
        'can write "248" or "1,204" or "248 of 3,000" without this piece deciding the wording. Hidden ' +
        'when empty.',
    },
    {
      name: 'description',
      attribute: 'description',
      type: 'text',
      description:
        'One line under the title saying what the surface holds. A shortcut: it is written into the ' +
        'same element that carries the default slot, so it REPLACES slotted description content rather ' +
        'than adding to it. Set one or the other.',
    },
  ],

  // Read out of the source rather than assumed: this piece attaches no listeners and dispatches
  // nothing. It displays what it is given, and the controls placed inside it raise their own events.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The description body, when it is richer than one line of text. Overwritten if the ' +
        '`description` attribute is set as well.',
    },
    { name: 'breadcrumb', takes: 'A Breadcrumb, above the eyebrow. The row collapses when nothing is in it.' },
    { name: 'actions', takes: 'The surface-level controls, pinned to the right of the title row.' },
  ],

  tokens: [
    { token: '--caos-color-border', controls: 'The rule drawn under the whole header.' },
    { token: '--caos-font-display', controls: 'The typeface of the title.' },
    { token: '--caos-text-h2', controls: 'The size of the title.' },
    { token: '--caos-font-mono', controls: 'The typeface of the eyebrow.' },
    { token: '--caos-color-text-muted', controls: 'The ink of the eyebrow, the count and the description.' },
    { token: '--caos-space-3', controls: 'The gap between the breadcrumb, the title row and the description.' },
  ],

  parts: [
    { part: 'header', is: 'the header block' },
    { part: 'breadcrumb', is: 'the area the trail is slotted into' },
    { part: 'eyebrow', is: 'the small line above the title' },
    { part: 'title', is: 'the heading' },
    { part: 'count', is: 'the count beside the heading' },
    { part: 'description', is: 'the line under the heading' },
    { part: 'actions', is: 'the action group at the right' },
  ],

  methods: [],

  placing:
    'First item on a list or overview surface, above whatever draws the records. It spans the full ' +
    'width of the region it is in and draws its own rule underneath, so nothing else needs to add a ' +
    'divider below it. It carries no record binding — the eyebrow, title, count and description are ' +
    'all supplied by the surface that placed it.',

  whenToUse:
    'The top of a surface that is ABOUT a set of things rather than about one record: a list view, a ' +
    'report, a Setup page, an overview.',

  insteadUse: [
    {
      name: 'RecordHeader',
      when:
        'The surface is ONE RECORD. That one leads with the object type, sets the record name in the ' +
        'accent beneath it, and carries the object mark, the status and the key fields a record needs. ' +
        'This one has nowhere to put any of that, and its title is a page name rather than a record name.',
    },
    {
      name: 'AppHome',
      when:
        'The surface is an app’s landing page. That is a whole assembled surface with its own hero, ' +
        'not a header you build a page underneath.',
    },
    {
      name: 'DetailSection',
      when: 'You want a heading for one block WITHIN a page rather than for the page itself.',
    },
    {
      name: 'Breadcrumb',
      when:
        'All you need is the trail back up. It goes INSIDE this piece’s breadcrumb slot rather than ' +
        'instead of it.',
    },
  ],

  accessibility:
    'The title is a real <h1>, so the surface has a document heading and a screen reader announces it ' +
    'as the page title without the author doing anything. The breadcrumb, eyebrow, count, description ' +
    'and actions rows each disappear from the layout entirely when nothing is in them, so an empty ' +
    'slot leaves no announced-but-blank region behind. What is left to the author: only ONE <h1> ' +
    'belongs on a surface, so do not place two of these on one page; and controls put in the actions ' +
    'slot need labels that still make sense read on their own, away from the title.',

  examples: [
    {
      key: 'list-head',
      label: 'The head of a list',
      summary:
        'All four text inputs set as attributes — the shape a list view uses, with the record count ' +
        'beside the title.',
      placement: {
        component: 'caos-page-header',
        inputs: {
          eyebrow: 'Sales Atlas',
          heading: 'Orders',
          count: '248',
          description: 'Every order raised across all accounts.',
        },
      },
    },
    {
      key: 'full',
      label: 'With a trail and actions',
      summary:
        'Every slot filled, and the description supplied as slotted content instead of as the ' +
        'attribute — the two paths are exclusive, and this is the other one.',
      placement: {
        component: 'caos-page-header',
        inputs: { eyebrow: 'Sales Atlas', heading: 'Orders', count: '248' },
        children: [
          {
            component: 'caos-breadcrumb',
            inputs: {
              slot: 'breadcrumb',
              items: [{ label: 'Sales Atlas', href: '/sales-atlas' }, { label: 'Orders' }],
            },
          },
          'Every order raised across all accounts, newest first.',
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'secondary' }, children: ['Export'] },
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'primary' }, children: ['New order'] },
        ],
      },
    },
  ],
};

export default docs;
